import React, { useEffect, useMemo, useRef, useState } from "react";
import { Award, Bookmark, Info, RefreshCw, Target, Trash2, Trophy } from "lucide-react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import {
  MAX_CUSTOM_OUTCOMES,
  copyTemplateGrading,
  createGradingPreset,
  createOutcomeId,
  gradingForTemplate,
  validateAssessmentOutcomeConfig,
  validateGrading,
} from "../utils/assessmentOutcomeConfig";
import "./AssessmentOutcomesEditor.css";

const GRADING_OPTIONS = [
  ["scoreOnly", "Score Only"],
  ["passFail", "Pass / Fail"],
  ["gcse9to1", "GCSE 9–1"],
  ["dmpf", "Distinction / Merit / Pass / Fail"],
  ["custom", "Custom"],
];

const CONDITION_OPTIONS = [
  ["always", "Always, up to the maximum"],
  ["belowPercentage", "Only below a percentage"],
  ["belowGrade", "Only below a grade / outcome"],
];

const FINAL_POLICY_OPTIONS = [
  ["highest", "Highest attempt"],
  ["latest", "Latest attempt"],
  ["average", "Average of completed attempts"],
  ["first", "First attempt"],
];

const REATTEMPT_EXPLANATIONS = {
  disabled: "Students receive one attempt.",
  always: "Students may reattempt while attempts remain.",
  belowPercentage: "A reattempt is required when the latest completed attempt is below the selected percentage.",
  belowGrade: "A reattempt is required when the latest completed attempt is below the selected outcome.",
};

const FINAL_POLICY_EXPLANATIONS = {
  highest: "Uses the student's strongest completed result.",
  latest: "Uses the most recent completed result.",
  average: "Uses the arithmetic mean of completed attempt percentages.",
  first: "Keeps the original completed result.",
};

const clone = (value) => JSON.parse(JSON.stringify(value));

const AssessmentOutcomesEditor = ({ value, onChange, teacherUid, kicker = "Grading & reattempts" }) => {
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateMessage, setTemplateMessage] = useState(null);
  const [templateName, setTemplateName] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [templatePendingDelete, setTemplatePendingDelete] = useState(null);
  const [templateDeleting, setTemplateDeleting] = useState(false);
  const [templateDeleteError, setTemplateDeleteError] = useState("");
  const deleteTemplateTriggerRef = useRef(null);
  const deleteTemplateCancelRef = useRef(null);
  const validation = useMemo(() => validateAssessmentOutcomeConfig(value), [value]);
  const gradingValidation = useMemo(() => validateGrading(value.grading), [value.grading]);
  const gradingEnabled = value.grading.type !== "scoreOnly";
  const reattemptsEnabled = value.reattempt.mode !== "disabled";
  const isCustom = value.grading.type === "custom";
  const reattemptCount = Number(value.reattempt.maxReattempts) || 0;
  const selectedTemplate = templates.find(({ id }) => id === selectedTemplateId) || null;

  useEffect(() => {
    let cancelled = false;
    if (!teacherUid) return undefined;
    const load = async () => {
      setTemplatesLoading(true);
      try {
        const snapshot = await getDocs(query(
          collection(db, "gradingSchemeTemplates"),
          where("teacherUid", "==", teacherUid)
        ));
        if (!cancelled) {
          setTemplates(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
        }
      } catch (error) {
        if (!cancelled) setTemplateMessage({ type: "error", text: "Saved schemes could not be loaded." });
      } finally {
        if (!cancelled) setTemplatesLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [teacherUid]);

  useEffect(() => {
    if (!templatePendingDelete) return undefined;
    const triggerElement = deleteTemplateTriggerRef.current;
    deleteTemplateCancelRef.current?.focus();
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !templateDeleting) {
        setTemplatePendingDelete(null);
        setTemplateDeleteError("");
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      triggerElement?.focus();
    };
  }, [templatePendingDelete, templateDeleting]);

  const emit = (next) => onChange(next);

  const changeGradingType = (type) => {
    const grading = createGradingPreset(type);
    const currentThreshold = value.reattempt.thresholdOutcomeId;
    const validThreshold = grading.outcomes.some(({ id }, index) =>
      id === currentThreshold && index < grading.outcomes.length - 1
    );
    let reattempt = value.reattempt;
    if (value.reattempt.mode === "belowGrade") {
      reattempt = type === "scoreOnly"
        ? { ...value.reattempt, mode: "belowPercentage", thresholdPercentage: "", thresholdOutcomeId: null }
        : { ...value.reattempt, thresholdOutcomeId: validThreshold ? currentThreshold : null };
    }
    emit({
      ...value,
      grading,
      reattempt,
    });
  };

  const updateOutcome = (index, field, nextValue) => {
    const outcomes = value.grading.outcomes.map((outcome, outcomeIndex) =>
      outcomeIndex === index ? { ...outcome, [field]: nextValue } : outcome
    );
    emit({ ...value, grading: { ...value.grading, sourceTemplateId: null, outcomes } });
  };

  const addCustomOutcome = () => {
    if (value.grading.outcomes.length >= MAX_CUSTOM_OUTCOMES) return;
    const outcomes = [...value.grading.outcomes];
    outcomes.splice(Math.max(0, outcomes.length - 1), 0, {
      id: createOutcomeId(),
      label: `Outcome ${outcomes.length}`,
      minPercentage: "",
    });
    emit({ ...value, grading: { ...value.grading, sourceTemplateId: null, outcomes } });
  };

  const removeCustomOutcome = (index) => {
    if (value.grading.outcomes.length <= 2 || index === value.grading.outcomes.length - 1) return;
    const removedId = value.grading.outcomes[index].id;
    const outcomes = value.grading.outcomes.filter((_, outcomeIndex) => outcomeIndex !== index);
    emit({
      ...value,
      grading: { ...value.grading, sourceTemplateId: null, outcomes },
      reattempt: value.reattempt.thresholdOutcomeId === removedId
        ? { ...value.reattempt, thresholdOutcomeId: null }
        : value.reattempt,
    });
  };

  const toggleReattempts = (enabled) => emit({
    ...value,
    reattempt: enabled
      ? { mode: "always", maxReattempts: 1, thresholdPercentage: null, thresholdOutcomeId: null }
      : { mode: "disabled", maxReattempts: 0, thresholdPercentage: null, thresholdOutcomeId: null },
    finalResultPolicy: enabled ? value.finalResultPolicy : "highest",
  });

  const changeReattemptMode = (mode) => emit({
    ...value,
    reattempt: {
      mode,
      maxReattempts: Math.max(1, Number(value.reattempt.maxReattempts) || 1),
      thresholdPercentage: mode === "belowPercentage" ? "" : null,
      thresholdOutcomeId: mode === "belowGrade" ? null : null,
    },
  });

  const applyTemplate = (templateId) => {
    setSelectedTemplateId(templateId);
    if (!templateId) return;
    const template = templates.find(({ id }) => id === templateId);
    if (!template) return;
    try {
      const grading = copyTemplateGrading(template.id, template);
      emit({
        ...value,
        grading,
        reattempt: value.reattempt.mode === "belowGrade"
          ? { ...value.reattempt, thresholdOutcomeId: null }
          : value.reattempt,
      });
      setTemplateMessage({ type: "success", text: `Copied “${template.name}” into this test.` });
    } catch (error) {
      setTemplateMessage({ type: "error", text: error.message });
    }
  };

  const requestTemplateDelete = () => {
    if (!selectedTemplate) return;
    if (!teacherUid || selectedTemplate.teacherUid !== teacherUid) {
      setTemplateMessage({ type: "error", text: "You can only delete grading schemes saved by your account." });
      return;
    }
    setTemplateMessage(null);
    setTemplateDeleteError("");
    setTemplatePendingDelete(selectedTemplate);
  };

  const cancelTemplateDelete = () => {
    if (templateDeleting) return;
    setTemplatePendingDelete(null);
    setTemplateDeleteError("");
  };

  const confirmTemplateDelete = async () => {
    if (!templatePendingDelete || templateDeleting) return;
    if (!teacherUid || templatePendingDelete.teacherUid !== teacherUid) {
      setTemplateDeleteError("You can only delete grading schemes saved by your account.");
      return;
    }
    setTemplateDeleting(true);
    setTemplateDeleteError("");
    try {
      await deleteDoc(doc(db, "gradingSchemeTemplates", templatePendingDelete.id));
      setTemplates((current) => current.filter(({ id }) => id !== templatePendingDelete.id));
      if (selectedTemplateId === templatePendingDelete.id) setSelectedTemplateId("");
      setTemplateMessage({ type: "success", text: `Deleted “${templatePendingDelete.name}” from your saved schemes.` });
      setTemplatePendingDelete(null);
    } catch (error) {
      setTemplateDeleteError("This saved scheme could not be deleted. Please try again.");
    } finally {
      setTemplateDeleting(false);
    }
  };

  const saveTemplate = async () => {
    setTemplateMessage(null);
    try {
      const grading = gradingForTemplate(value.grading, templateName);
      const payload = {
        schemaVersion: 1,
        teacherUid,
        name: templateName.trim(),
        grading,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      const reference = await addDoc(collection(db, "gradingSchemeTemplates"), payload);
      setTemplates((current) => [...current, { id: reference.id, ...clone(payload) }]);
      setTemplateName("");
      setTemplateMessage({ type: "success", text: "Reusable grading scheme saved." });
    } catch (error) {
      setTemplateMessage({ type: "error", text: error.message || "Reusable scheme could not be saved." });
    }
  };

  return (
    <section className="assessment-outcomes-editor" aria-labelledby="assessment-outcomes-title">
      <header className="assessment-outcomes-header">
        <span className="assessment-outcomes-hero-icon" aria-hidden="true"><Target size={22} /></span>
        <div>
          <p className="assessment-outcomes-kicker">{kicker}</p>
          <h2 id="assessment-outcomes-title">Assessment Outcomes</h2>
          <p>Choose how this assessment is graded, when students may reattempt it, and how their overall result is calculated.</p>
        </div>
      </header>

      <div className="assessment-outcomes-panel assessment-outcomes-grading">
        <div className="assessment-outcomes-subsection-heading">
          <span className="assessment-outcomes-subsection-icon grading" aria-hidden="true"><Award size={18} /></span>
          <div><h3>Grading</h3><p>Choose how student achievement is reported.</p></div>
        </div>

        <div className="assessment-outcomes-primary-control">
          <label className="assessment-outcomes-field">
            <span>Grading method</span>
            <select
              aria-label="Grading method"
              value={value.grading.type}
              onChange={(event) => changeGradingType(event.target.value)}
            >
              {GRADING_OPTIONS.map(([type, label]) => <option key={type} value={type}>{label}</option>)}
            </select>
            <small>Select score-only reporting or apply an outcome-based grading structure.</small>
          </label>
        </div>

        {gradingEnabled && (
          <div className="assessment-outcomes-info" role="note">
            <Info size={17} aria-hidden="true" />
            <p><strong>Set boundaries for this assessment.</strong> Presets provide structure only and are not official examination-board boundaries.</p>
          </div>
        )}

        {gradingEnabled && (
          <>
            <div className="assessment-outcomes-template-area">
              <label className="assessment-outcomes-field">
                <span>Saved scheme</span>
                <span className="assessment-outcomes-saved-scheme-control">
                <select
                  aria-label="Saved grading scheme"
                  value={selectedTemplateId}
                  disabled={templatesLoading}
                  onChange={(event) => applyTemplate(event.target.value)}
                >
                  <option value="">{templatesLoading ? "Loading saved schemes…" : "Select a saved scheme"}</option>
                  {templates.map((template) => <option key={template.id} value={template.id}>{template.name}</option>)}
                </select>
                  {selectedTemplate && (
                    <button
                      type="button"
                      ref={deleteTemplateTriggerRef}
                      className="assessment-outcomes-delete-template"
                      aria-label="Delete saved grading scheme"
                      title="Delete saved grading scheme"
                      disabled={templateDeleting}
                      onClick={requestTemplateDelete}
                    >
                      <Trash2 size={16} aria-hidden="true" />
                    </button>
                  )}
                </span>
              </label>
              <div className="assessment-outcomes-save-template">
                <label className="assessment-outcomes-field">
                  <span>Save current scheme</span>
                  <input
                    type="text"
                    value={templateName}
                    maxLength={100}
                    placeholder="e.g. Year 9 Computing"
                    onChange={(event) => setTemplateName(event.target.value)}
                  />
                </label>
                <button type="button" onClick={saveTemplate} disabled={!templateName.trim() || !gradingValidation.isValid}>
                  <Bookmark size={15} aria-hidden="true" />Save scheme
                </button>
              </div>
            </div>

            {templateMessage && <p className={`assessment-outcomes-message ${templateMessage.type}`} role="status">{templateMessage.text}</p>}

            <div className="assessment-outcomes-boundaries">
              <div className="assessment-outcomes-boundary-heading">
                <div><strong>Outcome boundaries</strong><span>Enter minimum percentages from highest to lowest.</span></div>
                {isCustom && (
                  <button type="button" onClick={addCustomOutcome} disabled={value.grading.outcomes.length >= MAX_CUSTOM_OUTCOMES}>
                    Add outcome
                  </button>
                )}
              </div>
              <div className="assessment-outcomes-boundary-columns" aria-hidden="true">
                <span>Outcome</span><span>Minimum</span><span></span>
              </div>
              {value.grading.outcomes.map((outcome, index) => {
                const isLowest = index === value.grading.outcomes.length - 1;
                return (
                  <div className="assessment-outcomes-boundary-row" key={outcome.id}>
                    <input
                      aria-label={`Outcome ${index + 1} label`}
                      type="text"
                      maxLength={60}
                      value={outcome.label}
                      disabled={!isCustom}
                      onChange={(event) => updateOutcome(index, "label", event.target.value)}
                    />
                    <label>
                      <span>Minimum</span>
                      <input
                        aria-label={`${outcome.label || `Outcome ${index + 1}`} minimum percentage`}
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={outcome.minPercentage}
                        disabled={isLowest}
                        onChange={(event) => updateOutcome(index, "minPercentage", event.target.value)}
                      />
                      <span>%</span>
                    </label>
                    {isCustom && !isLowest && (
                      <button type="button" className="assessment-outcomes-remove" onClick={() => removeCustomOutcome(index)} disabled={value.grading.outcomes.length <= 2}>
                        Remove
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      <div className="assessment-outcomes-panel assessment-outcomes-reattempts">
        <div className="assessment-outcomes-toggle-row">
          <div className="assessment-outcomes-subsection-heading">
            <span className="assessment-outcomes-subsection-icon reattempts" aria-hidden="true"><RefreshCw size={18} /></span>
            <div><h3>Reattempts</h3><p>Control whether students can make another attempt.</p></div>
          </div>
          <button
            type="button"
            className={`assessment-outcomes-switch ${reattemptsEnabled ? "active" : ""}`}
            role="switch"
            aria-checked={reattemptsEnabled}
            onClick={() => toggleReattempts(!reattemptsEnabled)}
          >
            {reattemptsEnabled ? "Enabled" : "Disabled"}
          </button>
        </div>

        <p className="assessment-outcomes-mode-explanation">{REATTEMPT_EXPLANATIONS[value.reattempt.mode]}</p>

        {reattemptsEnabled && (
          <>
            <div className="assessment-outcomes-policy-grid">
              <label className="assessment-outcomes-field">
                <span>Reattempt condition</span>
                <select aria-label="Reattempt condition" value={value.reattempt.mode} onChange={(event) => changeReattemptMode(event.target.value)}>
                  {CONDITION_OPTIONS
                    .filter(([mode]) => mode !== "belowGrade" || gradingEnabled)
                    .map(([mode, label]) => <option key={mode} value={mode}>{label}</option>)}
                </select>
              </label>
              <label className="assessment-outcomes-field">
                <span>Maximum reattempts</span>
                <input
                  aria-label="Maximum reattempts"
                  type="number"
                  min="1"
                  max="10"
                  step="1"
                  value={value.reattempt.maxReattempts}
                  onChange={(event) => emit({ ...value, reattempt: { ...value.reattempt, maxReattempts: event.target.value } })}
                />
                <small>{reattemptCount} reattempt{reattemptCount === 1 ? "" : "s"} = up to {reattemptCount + 1} total attempts</small>
              </label>

              {value.reattempt.mode === "belowPercentage" && (
                <label className="assessment-outcomes-field assessment-outcomes-threshold-field">
                  <span>Required percentage</span>
                  <span className="assessment-outcomes-inline-number">
                    <input
                      aria-label="Reattempt percentage threshold"
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={value.reattempt.thresholdPercentage ?? ""}
                      onChange={(event) => emit({ ...value, reattempt: { ...value.reattempt, thresholdPercentage: event.target.value } })}
                    /><span>%</span>
                  </span>
                </label>
              )}

              {value.reattempt.mode === "belowGrade" && (
                <label className="assessment-outcomes-field assessment-outcomes-threshold-field">
                  <span>Required outcome</span>
                  <select
                    aria-label="Reattempt grade threshold"
                    value={value.reattempt.thresholdOutcomeId || ""}
                    onChange={(event) => emit({ ...value, reattempt: { ...value.reattempt, thresholdOutcomeId: event.target.value || null } })}
                  >
                    <option value="">Select an outcome</option>
                    {value.grading.outcomes.slice(0, -1).map((outcome) => (
                      <option key={outcome.id} value={outcome.id}>{outcome.label}</option>
                    ))}
                  </select>
                </label>
              )}
            </div>

            <div className="assessment-outcomes-feedback-note" role="note">
              <Info size={17} aria-hidden="true" />
              <p>Detailed feedback and answer guidance remain locked while another attempt can still be started. Feedback becomes available once the student meets the requirement or completes all permitted attempts.</p>
            </div>
          </>
        )}
      </div>

      <div className={`assessment-outcomes-panel assessment-outcomes-final-result ${reattemptsEnabled ? "" : "is-muted"}`}>
        <div className="assessment-outcomes-subsection-heading">
          <span className="assessment-outcomes-subsection-icon final" aria-hidden="true"><Trophy size={18} /></span>
          <div><h3>Final result</h3><p>Choose how completed attempts are combined into the student's overall result.</p></div>
        </div>
        <label className="assessment-outcomes-field assessment-outcomes-final-control">
          <span>Final result method</span>
          <select
            aria-label="Final result method"
            value={value.finalResultPolicy}
            disabled={!reattemptsEnabled}
            onChange={(event) => emit({ ...value, finalResultPolicy: event.target.value })}
          >
            {FINAL_POLICY_OPTIONS.map(([policy, label]) => <option key={policy} value={policy}>{label}</option>)}
          </select>
          <small>{reattemptsEnabled ? FINAL_POLICY_EXPLANATIONS[value.finalResultPolicy] : "Enable reattempts to choose how multiple completed attempts are combined."}</small>
        </label>
      </div>

      {!validation.isValid && (
        <div className="assessment-outcomes-errors" role="alert">
          <strong>Assessment Outcomes needs attention</strong>
          <ul>{validation.errors.map((error) => <li key={error}>{error}</li>)}</ul>
        </div>
      )}

      {templatePendingDelete && (
        <div
          className="assessment-outcomes-dialog-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) cancelTemplateDelete();
          }}
        >
          <div
            className="assessment-outcomes-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-grading-scheme-title"
            aria-describedby="delete-grading-scheme-description"
          >
            <span className="assessment-outcomes-dialog-icon" aria-hidden="true"><Trash2 size={20} /></span>
            <div className="assessment-outcomes-dialog-copy">
              <h3 id="delete-grading-scheme-title">Delete saved grading scheme?</h3>
              <p id="delete-grading-scheme-description">Are you sure you want to delete <strong>“{templatePendingDelete.name}”</strong>?</p>
              <p>This will remove it from your saved schemes. It will not change tests that already use these boundaries.</p>
            </div>
            {templateDeleteError && <p className="assessment-outcomes-dialog-error" role="alert">{templateDeleteError}</p>}
            <div className="assessment-outcomes-dialog-actions">
              <button
                type="button"
                ref={deleteTemplateCancelRef}
                className="assessment-outcomes-dialog-cancel"
                disabled={templateDeleting}
                onClick={cancelTemplateDelete}
              >
                Cancel
              </button>
              <button
                type="button"
                className="assessment-outcomes-dialog-confirm"
                disabled={templateDeleting}
                onClick={confirmTemplateDelete}
              >
                <Trash2 size={15} aria-hidden="true" />{templateDeleting ? "Deleting…" : "Delete scheme"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default AssessmentOutcomesEditor;
